import { Component, EventEmitter, Input, Output } from '@angular/core';
import { openClose } from '@core/animations/animations';
import { Address } from '@models/address';
import {
	BusinessClient,
	ClientCreationData,
	PersonClient,
	Client,
} from '@models/clients';
import { LawFirm } from '@models/lawFirms';
import { MatterClientType } from '@models/matter';
import {
	CorrespondenceType,
	EncumbranceCorrespondence,
	EncumbranceCorrespondenceType,
	Mortgagee,
	PrivateLender,
} from '@models/lender';
import { ClientsRepoService } from '@repos/clients/clients.service';
import { cloneDeep } from 'lodash';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import { ICON } from '@core/util/icon';
import { clientLegalName } from '@core/util/client';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BusinessState } from '@state/business';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { MatterClientDispatch } from '@state/matter_clients/matterClients.dispatch';
import { SelectedMatter } from '@state/matter/matter.selectors';

const CorrespondenceOptions: RadioGroupItem<EncumbranceCorrespondenceType>[] = [
	{ label: 'Mortgagee', value: EncumbranceCorrespondenceType.MORTGAGEE },
	{ label: 'Solicitor', value: EncumbranceCorrespondenceType.SOLICITOR },
];

@Component({
	selector: 'app-private-lender',
	templateUrl: './private-lender.component.html',
	animations: [openClose()],
	standalone: false,
})
export class EncumbrancePrivateLenderComponent {
	public ICON = ICON;
	public correspondenceOptions = CorrespondenceOptions;
	public correspondenceTypes = CorrespondenceType;
	public _mortgagee: Mortgagee;

	@SelectSnapshot(BusinessState.getNameFormattingSettings)
	nameFormattingSettings: NameFormattingSettings;

	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;

	@Input() set mortgagee(mortgagee: Mortgagee) {
		this._mortgagee = cloneDeep(mortgagee);

		if (!this._mortgagee?.privateLender) {
			this._mortgagee.privateLender = {} as PrivateLender;
		}

		if (!this._mortgagee?.correspondence) {
			this._mortgagee.correspondence = {} as EncumbranceCorrespondence;
		}
	}

	@Output() updateMortgagee: EventEmitter<Mortgagee> = new EventEmitter();

	constructor(
		private clientRepo: ClientsRepoService,
		private matterClientDispatch: MatterClientDispatch,
	) {}

	updateSolicitor(value: LawFirm) {
		this._mortgagee.solicitor = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setCorrespondenceType(value: EncumbranceCorrespondenceType) {
		this._mortgagee.correspondenceType = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	addressChanged(value: Address) {
		this._mortgagee.privateLender.address = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setFullName(value: string) {
		this._mortgagee.privateLender.fullName = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setLenderReLine(value: string) {
		this._mortgagee.correspondence.lenderRELine = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	SetPrivateLenderBank(value: string) {
		this._mortgagee.privateLender.banking.bank = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	SetPrivateLenderTransitNumber(value: string) {
		this._mortgagee.privateLender.banking.transitNumber = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	SetPrivateLenderInstitutionNumber(value: string) {
		this._mortgagee.privateLender.banking.institutionNumber = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	SetPrivateLenderAccountNumber(value: string) {
		this._mortgagee.privateLender.banking.accountNumber = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	SetPrivateLenderWorkPhone(value: string) {
		this._mortgagee.privateLender.workPhone = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	SetPrivateLenderPhone(value: string) {
		this._mortgagee.privateLender.phone = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	SetPrivateLenderFax(value: string) {
		this._mortgagee.privateLender.fax = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	SetPrivateLenderEmail(value: string) {
		this._mortgagee.privateLender.email = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	async SetPrivateLender(ev: Client) {
		if (this.isLeapMatter) {
			this.onAddPrivateLender({ client: ev, matterClientRelationship: {} });
			return;
		}
		const clientDOC = await this.clientRepo.Get(ev.id).toPromise();
		const client = clientDOC.data() as PersonClient | BusinessClient;
		this.updatePrivateLender(client);
	}

	onAddPrivateLender(clientCreationData: ClientCreationData) {
		const client = clientCreationData.client;
		this.matterClientDispatch.CreateAndAddClientToMatter({
			client: clientCreationData.client,
			matterClientRelationship: {
				...clientCreationData.matterClientRelationship,
				clientTypes: [MatterClientType.PRIVATE_LENDER],
			},
		});
		this.updatePrivateLender(client);
	}

	updatePrivateLender(client: Client) {
		const privateLender = {
			fullName: clientLegalName(client, this.nameFormattingSettings),
			banking: client?.banking ?? null,
			address: client?.serviceAddress ?? null,
			phone: client?.phone ?? null,
			workPhone: (client as PersonClient)?.work ?? null,
			email: client?.email ?? null,
		};
		this._mortgagee.privateLender = privateLender;
		this.updateMortgagee.emit(this._mortgagee);
	}
}
